import { ChangeEvent, FormEvent, useMemo, useState } from 'react'

import { requestListingsQuestion } from '../../_data/renter-llm'
import {
  Wrapper,
  HeaderRow,
  HeaderText,
  Eyebrow,
  Title,
  Form,
  Input,
  SubmitButton,
  Suggestions,
  SuggestionChip,
  AnswerCard,
  AnswerHeader,
  QuestionEcho,
  ConfidencePill,
  AnswerText,
  MatchedRow,
  MatchedChip,
  ErrorText,
  HintText,
} from './styled'
import {
  SUGGESTION_PROMPTS,
  buildCompactListingsForPrompt,
  confidenceLabel,
} from './helpers'
import {
  ListingsQuestionAnswerPayload,
  ListingsQuestionBarProps,
  ListingsQuestionBarState,
} from './types'

const MIN_LISTINGS_FOR_USEFUL_QA = 1

/**
 * "Ask anything about your places" bar for the saved-listings page.
 * Renters type a freeform question; the LLM answers using only their
 * tracked listing data and returns IDs of the matched listings as
 * clickable chips. Empty state, loading, offline, and error states are
 * all handled — never blocks the rest of the page.
 *
 * @param {ListingsQuestionBarProps} props
 * @returns {JSX.Element | null}
 */
const ListingsQuestionBar = ({
  listings,
  preferences,
  onMatchedListingClick,
}: ListingsQuestionBarProps): JSX.Element | null => {
  const [draft, setDraft] = useState<string>('')
  const [state, setState] = useState<ListingsQuestionBarState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [answer, setAnswer] = useState<ListingsQuestionAnswerPayload | null>(
    null,
  )

  const compactListings = useMemo(
    () => buildCompactListingsForPrompt(listings),
    [listings],
  )

  const matchedListings = useMemo(() => {
    if (answer === null) {
      return []
    }
    const idSet = new Set(answer.matchedListingIds)
    return listings.filter(listing => idSet.has(listing.id))
  }, [answer, listings])

  if (listings.length < MIN_LISTINGS_FOR_USEFUL_QA) {
    return null
  }

  const isWorking = state === 'loading'
  const trimmedDraft = draft.trim()
  const canSubmit = trimmedDraft.length > 0 && isWorking === false

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDraft(event.target.value)
  }

  const submitQuestion = async (question: string): Promise<void> => {
    setState('loading')
    setErrorMessage('')

    try {
      const response = await requestListingsQuestion({
        question,
        listings: compactListings,
        preferences,
      })

      if (response === null) {
        setState('offline')
        return
      }

      setAnswer({
        answer: response.answer,
        matchedListingIds: response.matchedListingIds,
        confidence: response.confidence,
        question,
      })
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not answer right now. Try again in a sec.',
      )
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (canSubmit === false) {
      return
    }
    void submitQuestion(trimmedDraft)
  }

  const handleSuggestion = (prompt: string): void => {
    setDraft(prompt)
    void submitQuestion(prompt)
  }

  const handleMatchedClick = (listingId: string): void => {
    if (onMatchedListingClick !== undefined) {
      onMatchedListingClick(listingId)
    }
  }

  return (
    <Wrapper>
      <HeaderRow>
        <HeaderText>
          <Eyebrow>Ask QuickPass</Eyebrow>
          <Title>Ask anything about your places</Title>
        </HeaderText>
      </HeaderRow>

      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          value={draft}
          onChange={handleDraftChange}
          placeholder="e.g. cheapest 2-bedroom with parking"
          aria-label="Question about your saved listings"
          disabled={isWorking === true}
        />
        <SubmitButton type="submit" disabled={canSubmit === false}>
          {isWorking === true ? 'Thinking…' : 'Ask'}
        </SubmitButton>
      </Form>

      {state === 'idle' && answer === null ? (
        <Suggestions>
          {SUGGESTION_PROMPTS.map(prompt => (
            <SuggestionChip
              key={prompt}
              type="button"
              onClick={() => handleSuggestion(prompt)}
            >
              {prompt}
            </SuggestionChip>
          ))}
        </Suggestions>
      ) : null}

      {state === 'offline' ? (
        <HintText>
          Backend not configured — the AI answer needs the LLM endpoint to be
          reachable.
        </HintText>
      ) : null}

      {state === 'error' ? <ErrorText>{errorMessage}</ErrorText> : null}

      {answer !== null ? (
        <AnswerCard>
          <AnswerHeader>
            <QuestionEcho>&ldquo;{answer.question}&rdquo;</QuestionEcho>
            <ConfidencePill confidence={answer.confidence}>
              {confidenceLabel(answer.confidence)}
            </ConfidencePill>
          </AnswerHeader>
          <AnswerText>{answer.answer}</AnswerText>
          {matchedListings.length > 0 ? (
            <MatchedRow>
              {matchedListings.map(listing => (
                <MatchedChip
                  key={listing.id}
                  type="button"
                  onClick={() => handleMatchedClick(listing.id)}
                >
                  {listing.title}
                </MatchedChip>
              ))}
            </MatchedRow>
          ) : null}
        </AnswerCard>
      ) : null}
    </Wrapper>
  )
}

export default ListingsQuestionBar
